import { defaultItemSorter, defaultPositionKey } from './defaults'
import { getResponseId, identifierFor, placeholderTypes } from './utils'

const updatedItem = ({ errors, map, updates }, item) => {
  const itemIdentifier = identifierFor(item, map)
  const itemUpdates = updates.filter(
    ({ identifier }) => itemIdentifier === identifier
  )
  const itemErrors = errors.filter(
    ({ identifier }) => itemIdentifier === identifier
  )

  return itemUpdates.reduce(
    (accum, { update }) => ({
      ...accum,
      ...update,
    }),
    {
      ...item,
      uuid: identifierFor(item, map),
      updates: itemUpdates,
      errors: itemErrors,
    }
  )
}

export const itemsWithUpdates = ({
  items = [],
  updates = [],
  errors = [],
  map = {},
  positionKey = defaultPositionKey,
  itemSorter = defaultItemSorter,
}) => {
  const additions = updates
    .filter(
      ({ identifier, update, item }) =>
        update &&
        !item &&
        !items.find((anItem) => identifierFor(anItem, map) === identifier)
    )
    .map((addition) => addition.update)

  const removals = updates
    .filter(({ item, update }) => item && !update)
    .map(({ identifier }) => identifier)

  return items
    .concat(additions)
    .filter((item) => !removals.includes(identifierFor(item, map)))
    .map(updatedItem.bind(this, { map, updates, errors }))
    .sort(itemSorter(positionKey))
}

// An untouched whenEmpty placeholder outlives the empty list it was made for,
// because it lives in updates rather than items.
const isUntouchedEmptyListPlaceholder = (updates, { identifier, update }) =>
  update?.emptyListPlaceholder &&
  updates.filter((other) => other.identifier === identifier).length === 1

export const sanitizeUpdates = (updates, editing, items) => {
  if (editing) {
    return items && items.length
      ? updates.filter(
          (update) => !isUntouchedEmptyListPlaceholder(updates, update)
        )
      : updates
  }

  return updates.filter(({ update }) => {
    if (!update) {
      return false
    }
    if (items && items.length) {
      return !update.autoGenerated
    }
    return true
  })
}

export const stateFromProps =
  ({
    editing,
    items = [],
    positionKey = defaultPositionKey,
    itemSorter = defaultItemSorter,
    placeholder = 'none',
  } = {}) =>
  ({ updates = [], errors = [], idMap = {} } = {}) => {
    const newMap = items.reduce(
      (map, item) => ({
        ...map,
        [item.id]: identifierFor(item, idMap),
      }),
      {}
    )

    const newUpdates = sanitizeUpdates(updates, editing, items)

    const newItems = itemsWithUpdates({
      items,
      updates: newUpdates,
      errors,
      idMap,
      positionKey,
      itemSorter,
    })

    if (editing || !newItems.length) {
      const placeholderItem = placeholderTypes[placeholder](newItems, {
        positionKey,
      })

      if (placeholderItem) {
        return {
          updates: newUpdates.concat({
            identifier: placeholderItem.uuid,
            update: placeholderItem,
          }),
          items: newItems.concat(placeholderItem).sort(itemSorter(positionKey)),
          idMap: newMap,
        }
      }
    }

    return {
      idMap: newMap,
      items: newItems,
      updates: newUpdates,
    }
  }

const updaterFilter = (orig, { updater }) => orig === updater
const notUpdaterFilter = (orig, { updater }) => orig !== updater

export const updateErrorReducer = (updater, error) => (prevState) => ({
  errors: (prevState.errors || []).concat(
    (prevState.updates || [])
      .filter(notUpdaterFilter.bind(this, updater))
      .concat(error)
  ),
  updates: (prevState.updates || []).filter(updaterFilter.bind(this, updater)),
})

export const updateCompleteReducer =
  ({ creator, identifier, props, response, updater }) =>
  (prevState) => {
    const filteredUpdates = (prevState.updates || []).filter(
      notUpdaterFilter.bind(this, updater)
    )

    const idMap = {
      ...prevState.idMap,
      [getResponseId(response)]: identifier,
    }

    if (creator) {
      return stateFromProps(props)({
        ...prevState,
        updates: filteredUpdates.filter(
          ({ identifier: ident, update }) =>
            ident !== identifier || !update || !update.isPlaceholder
        ),
      })
    }

    return stateFromProps(props)({
      ...prevState,
      updates: filteredUpdates,
      idMap,
    })
  }

export const trackUpdateReducer =
  ({ creator, identifier, item, props, update, updater }) =>
  ({ errors = [], idMap = {}, updates = [] }) => {
    const {
      editing,
      items,
      positionKey = defaultPositionKey,
      itemSorter = defaultItemSorter,
      placeholder = 'none',
    } = props

    const sanitizedUpdates = sanitizeUpdates(updates, editing, items)
    const newUpdate = {
      ...update,
      isPlaceholder: undefined,
      autoGenerated: undefined,
    }

    const newUpdates = sanitizedUpdates.concat({
      creator,
      identifier,
      item,
      update: update ? newUpdate : update,
      updater,
    })

    const newItems = itemsWithUpdates({
      errors,
      idMap,
      items,
      itemSorter,
      positionKey,
      updates: newUpdates,
    })

    if (editing || !newItems.length) {
      const placeholderItem = placeholderTypes[placeholder](newItems, {
        positionKey,
      })

      if (placeholderItem) {
        return {
          updates: newUpdates.concat({
            identifier: placeholderItem.uuid,
            update: placeholderItem,
          }),
          items: newItems.concat(placeholderItem).sort(itemSorter(positionKey)),
        }
      }
    }

    return {
      updates: newUpdates,
      items: newItems,
    }
  }
